import { setLoaded, setLoading, withCallState, withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Signal } from '@angular/core';
import { patchState, signalMethod, signalStore, withHooks, withMethods } from '@ngrx/signals';
import { Site, SpatialStore } from '@privaone/masterdata';

import { PrivaLocalizationService } from '@priva/localization';

import { ErrorHandlerService } from 'app/core/services/error-handling';
import { AuthStore } from 'app/core/stores';
import { handleError } from 'app/core/utils/store-helper-utils';

const SitesCollection = 'sites';
const EditSiteCollection = 'editSite';
export type SitesStore = InstanceType<typeof SitesStore>;

export const SitesStore = signalStore(
    { providedIn: 'root' },
    withCallState({ collection: SitesCollection }),
    withCallState({ collection: EditSiteCollection }),
    withDevtools('SitesStore'),
    withMethods((_, spatialStore = inject(SpatialStore)) => {
        const sitesEntities: Signal<Site[]> = computed(() => {
            const sites = spatialStore.sites();
            return sites.map((site) => ({
                ...site,
                greenhouseCountCount: site.greenhouseCount ?? 0,
                rowCount: site.rowCount ?? 0,
                compartmentCount: site.compartmentCount ?? 0,
            }));
        });

        const sitesEntityMap: Signal<Record<string, Site>> = computed(() => {
            const entities = sitesEntities();
            return Object.fromEntries(entities.map((site) => [site.id, site]));
        });

        return { sitesEntities, sitesEntityMap };
    }),
    withMethods((store) => {
        const authStore = inject(AuthStore);
        const spatialStore = inject(SpatialStore);
        const translate = inject(PrivaLocalizationService);
        const errorHandlerService = inject(ErrorHandlerService);

        async function loadSites(tenantId: string): Promise<void> {
            patchState(store, setLoading(SitesCollection));

            await spatialStore
                .loadSites(tenantId)
                .then(() => {
                    patchState(store, setLoaded(SitesCollection));
                })
                .catch(() => {
                    const translatedErrorMessage = translate.instant('ERROR.GLOBAL_ERROR_MESSAGE');
                    handleError(store, translatedErrorMessage, SitesCollection);
                    errorHandlerService.handleScriptMessages(translatedErrorMessage);
                });
        }

        const loadSitesAfterTenantIdAvailableObserved = signalMethod<boolean>(async (tenantIdAvailable) => {
            if (!tenantIdAvailable || store.sitesCallState() !== 'init') {
                return;
            }

            const tenantId = authStore.tenantId();
            if (!tenantId) {
                return;
            }

            await loadSites(tenantId!);
        });

        return { loadSites, loadSitesAfterTenantIdAvailableObserved };
    }),
    withHooks((store) => {
        const authStore = inject(AuthStore);

        return {
            onInit() {
                store.loadSitesAfterTenantIdAvailableObserved(authStore.isTenantIdAvailable);
            },
        };
    }),
);
